import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Sun,
  Wind,
  BatteryCharging,
  ShieldCheck,
  CheckCircle2,
  Clock,
  Activity,
  TrendingUp,
  RotateCcw,
  Gauge,
  Sparkles,
  Plus,
  Minus,
  ArrowDown,
  Layers,
  FileCheck2,
  AlertTriangle,
} from 'lucide-react';
import { Navbar } from '../../components/common/Navbar';
import { Footer } from '../../components/common/Footer';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';
import { SPAQ_CORE_FAQS, CASES_DATA } from '../../data/siteData';
import { SEO } from '../../components/common/SEO';

/**
 * 画面イメージ再現用：電力デマンド推移グラフコンポーネント
 */
const DemandChartMock: React.FC = () => {
  return (
    <div className="rounded-2xl bg-[#091D30]/95 border border-white/15 p-5 sm:p-6 shadow-2xl backdrop-blur-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-spaq-cyan-bright" />
          <span className="text-sm font-bold text-white tracking-wide">電力デマンド推移</span>
        </div>
        <span className="text-[11px] text-slate-400 font-mono bg-white/5 border border-white/10 px-2 py-0.5 rounded">
          画面イメージ
        </span>
      </div>

      <div className="relative w-full aspect-[16/10] bg-[#071625] rounded-xl border border-white/10 p-3 sm:p-4 overflow-hidden">
        <svg viewBox="0 0 500 280" className="w-full h-full" preserveAspectRatio="none">
          <defs>
            <linearGradient id="pvGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#BA9C5B" stopOpacity="0.45" />
              <stop offset="100%" stopColor="#BA9C5B" stopOpacity="0.0" />
            </linearGradient>
            <linearGradient id="demandGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#38B5DE" stopOpacity="0.25" />
              <stop offset="100%" stopColor="#38B5DE" stopOpacity="0.0" />
            </linearGradient>
          </defs>

          {/* Y軸グリッド */}
          <line x1="40" y1="50" x2="480" y2="50" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
          <line x1="40" y1="100" x2="480" y2="100" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
          <line x1="40" y1="150" x2="480" y2="150" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
          <line x1="40" y1="200" x2="480" y2="200" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
          <line x1="40" y1="240" x2="480" y2="240" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />

          {/* 目標デマンド・契約電力（破線） */}
          <line x1="40" y1="80" x2="480" y2="80" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" strokeDasharray="5,5" />
          <text x="475" y="74" fill="rgba(255,255,255,0.55)" fontSize="10" textAnchor="end" fontFamily="monospace">目標デマンド</text>

          {/* Y軸ラベル */}
          <text x="32" y="54" fill="rgba(255,255,255,0.4)" fontSize="10" textAnchor="end" fontFamily="monospace">300</text>
          <text x="32" y="104" fill="rgba(255,255,255,0.4)" fontSize="10" textAnchor="end" fontFamily="monospace">200</text>
          <text x="32" y="154" fill="rgba(255,255,255,0.4)" fontSize="10" textAnchor="end" fontFamily="monospace">100</text>
          <text x="32" y="244" fill="rgba(255,255,255,0.4)" fontSize="10" textAnchor="end" fontFamily="monospace">0</text>

          {/* X軸ラベル */}
          <text x="45" y="260" fill="rgba(255,255,255,0.4)" fontSize="10" textAnchor="start" fontFamily="monospace">0:00</text>
          <text x="145" y="260" fill="rgba(255,255,255,0.4)" fontSize="10" textAnchor="middle" fontFamily="monospace">6:00</text>
          <text x="250" y="260" fill="rgba(255,255,255,0.4)" fontSize="10" textAnchor="middle" fontFamily="monospace">12:00</text>
          <text x="355" y="260" fill="rgba(255,255,255,0.4)" fontSize="10" textAnchor="middle" fontFamily="monospace">18:00</text>
          <text x="475" y="260" fill="rgba(255,255,255,0.4)" fontSize="10" textAnchor="end" fontFamily="monospace">24:00</text>

          {/* 太陽光（ゴールド） */}
          <path
            d="M 120 240 Q 180 240 210 180 Q 250 85 290 180 Q 320 240 380 240 Z"
            fill="url(#pvGrad)"
          />
          <path
            d="M 120 240 Q 180 240 210 180 Q 250 85 290 180 Q 320 240 380 240"
            fill="none"
            stroke="#BA9C5B"
            strokeWidth="2.5"
          />

          {/* 空調負荷（スカイブルー破線） */}
          <path
            d="M 45 220 Q 140 215 180 180 Q 240 145 320 150 Q 390 160 475 220"
            fill="none"
            stroke="#60A5FA"
            strokeWidth="2"
            strokeDasharray="4,2"
          />

          {/* 蓄電池（充放電）（エメラルド） */}
          <path
            d="M 45 225 Q 100 230 140 220 Q 200 200 250 165 Q 310 140 380 180 Q 420 210 475 230"
            fill="none"
            stroke="#10B981"
            strokeWidth="2"
          />

          {/* 全体の電力需要（シアンブルー実線）- ピークカット制御 */}
          <path
            d="M 45 200 Q 120 185 160 140 Q 210 92 250 90 Q 290 90 340 96 Q 400 130 475 190 L 475 240 L 45 240 Z"
            fill="url(#demandGrad)"
          />
          <path
            d="M 45 200 Q 120 185 160 140 Q 210 92 250 90 Q 290 90 340 96 Q 400 130 475 190"
            fill="none"
            stroke="#38B5DE"
            strokeWidth="3"
          />
        </svg>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4 pt-3 border-t border-white/10 text-[11px]">
        <div className="flex items-center gap-1.5 text-slate-300">
          <span className="w-2.5 h-1 rounded-full bg-[#38B5DE]" />
          <span>全体の電力需要</span>
        </div>
        <div className="flex items-center gap-1.5 text-slate-300">
          <span className="w-2.5 h-1 rounded-full bg-[#BA9C5B]" />
          <span>太陽光発電量</span>
        </div>
        <div className="flex items-center gap-1.5 text-slate-300">
          <span className="w-2.5 h-1 rounded-full bg-[#60A5FA]" />
          <span>空調負荷</span>
        </div>
        <div className="flex items-center gap-1.5 text-slate-300">
          <span className="w-2.5 h-1 rounded-full bg-[#10B981]" />
          <span>蓄電池（充放電）</span>
        </div>
      </div>
    </div>
  );
};

export default function SpaqCorePage() {
  const [activeTab, setActiveTab] = useState<'overview' | 'predictive' | 'safety' | 'cases' | 'faq'>('overview');
  const [openAccordions, setOpenAccordions] = useState<{ [key: string]: boolean }>({
    'pred-nature': false,
    'pred-details': false,
    'overview-challenges': false,
    'overview-sequence': false,
  });
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [openSafetyItem, setOpenSafetyItem] = useState<string | null>('edge');

  const scrollToSection = (id: string) => {
    setActiveTab(id as any);
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -90;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const toggleAccordion = (key: string) => {
    setOpenAccordions((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <div className="min-h-screen flex flex-col bg-white text-navy font-sans antialiased">
      <SEO
        title="SPAQ CORE｜現場の電力運用・協調制御システム"
        description="太陽光、蓄電池、空調を現場で自律連携。デマンドピーク抑制と電力コスト最適化を実現するSPAQ COREのご紹介。"
        canonicalPath="/products/spaq-core"
      />
      <Navbar />

      <main className="flex-1">
        {/* ============================================================ */}
        {/* 1. HERO SECTION (Dark Editorial Theme)                       */}
        {/* ============================================================ */}
        <section className="bg-[#061523] text-white border-b border-white/10 pt-8 pb-14 md:pt-12 md:pb-20 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_70%_at_50%_-15%,rgba(37,168,208,0.18),rgba(255,255,255,0))] pointer-events-none" />

          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            {/* パンくずリスト */}
            <nav className="flex items-center gap-2 text-xs text-slate-400 mb-6 font-medium">
              <Link to="/" className="hover:text-white transition-colors">ホーム</Link>
              <span>/</span>
              <Link to="/business" className="hover:text-white transition-colors">事業・サービス</Link>
              <span>/</span>
              <span className="text-spaq-cyan-bright">SPAQ CORE</span>
            </nav>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
              {/* 左カラム：テキスト & CTA */}
              <div className="lg:col-span-6 space-y-6">
                <div>
                  <span className="text-xs font-mono font-bold text-spaq-cyan-bright tracking-widest uppercase">
                    CORE TECHNOLOGY
                  </span>
                  <h1 className="text-3xl sm:text-4xl lg:text-[46px] font-bold tracking-tight text-white leading-[1.18] mt-2">
                    SPAQ CORE
                    <span className="block text-2xl sm:text-3xl lg:text-[34px] font-bold text-slate-100 mt-2">
                      電力ピークを捉え、
                      <br />
                      <span className="text-spaq-cyan-bright">必要なタイミングで制御する。</span>
                    </span>
                  </h1>
                </div>

                <div className="space-y-3.5 text-slate-300 text-sm sm:text-base leading-relaxed">
                  <p className="text-base sm:text-lg font-medium text-white/95">
                    太陽光・空調・蓄電池を、ひとつの知性で。
                    <br className="hidden sm:inline" />
                    設備をつなぎ、現場の快適性に配慮しながら30分デマンドの抑制を支えます。
                  </p>
                  <p className="text-xs sm:text-sm text-slate-400">
                    <strong className="text-slate-200">「SPAQ CORE」</strong>
                    は、工場・倉庫・商業施設などの現場で、太陽光発電・空調機器（デマンドコントローラー）・蓄電池を自律連携。複数の時間軸による需要予測と、30分間の電力量に基づく予測充放電を搭載し、ピークを抑えるための先行放電から、次のピークに備えた残量回復まで、現場の電力変化に合わせて自動調整します。
                  </p>
                </div>

                <div className="pt-2 flex flex-col sm:flex-row gap-3.5">
                  <Button
                    to="/contact?type=spaq-core"
                    variant="primary"
                    size="lg"
                    className="bg-spaq-cyan hover:bg-spaq-cyan-bright text-white shadow-cyan-glow border-none"
                    icon={<ArrowRight className="w-4 h-4" />}
                  >
                    導入について相談する
                  </Button>
                  <button
                    onClick={() => scrollToSection('overview')}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl border border-white/20 bg-white/5 hover:bg-white/10 text-white text-sm font-semibold transition-colors"
                  >
                    <span>仕組みを見る</span>
                    <ArrowDown className="w-4 h-4 text-spaq-cyan-bright" />
                  </button>
                </div>
              </div>

              {/* 右カラム：電力デマンド推移（SVGグラフィックカード） */}
              <div className="lg:col-span-6">
                <DemandChartMock />
              </div>
            </div>

            {/* Hero下部：3要素アイコンバー */}
            <div className="mt-12 pt-8 border-t border-white/10 grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-center gap-3.5">
                <div className="w-11 h-11 rounded-xl bg-spaq-gold/15 border border-spaq-gold/30 flex items-center justify-center text-spaq-gold shrink-0">
                  <Sun className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs font-bold text-spaq-gold">太陽光</div>
                  <div className="text-sm font-bold text-white">発電電力を有効活用</div>
                  <p className="text-xs text-slate-400">自家消費でベース購入電力を圧縮</p>
                </div>
              </div>

              <div className="flex items-center gap-3.5">
                <div className="w-11 h-11 rounded-xl bg-spaq-cyan/15 border border-spaq-cyan/30 flex items-center justify-center text-spaq-cyan-bright shrink-0">
                  <Wind className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs font-bold text-spaq-cyan-bright">空調</div>
                  <div className="text-sm font-bold text-white">快適性を保ちながら制御</div>
                  <p className="text-xs text-slate-400">段階的な緩和で無理な全止めを回避</p>
                </div>
              </div>

              <div className="flex items-center gap-3.5">
                <div className="w-11 h-11 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shrink-0">
                  <BatteryCharging className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs font-bold text-emerald-400">蓄電池</div>
                  <div className="text-sm font-bold text-white">必要なタイミングで充放電</div>
                  <p className="text-xs text-slate-400">山場へのピンポイント放電と予測充電</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============================================================ */}
        {/* 2. STICKY ANCHOR NAVIGATION BAR                             */}
        {/* ============================================================ */}
        <div className="sticky top-20 z-40 bg-white/95 backdrop-blur-md border-b border-surface-border shadow-xs">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center gap-3 sm:gap-10 overflow-x-auto py-3 no-scrollbar text-xs sm:text-sm font-medium">
              {[
                { id: 'overview', label: '特長' },
                { id: 'predictive', label: '予測充放電' },
                { id: 'safety', label: '安全設計' },
                { id: 'cases', label: '事例・試算' },
                { id: 'faq', label: 'よくあるご質問' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => scrollToSection(tab.id)}
                  className={`px-3 py-1.5 transition-colors whitespace-nowrap relative ${
                    activeTab === tab.id
                      ? 'text-spaq-cyan-dark font-bold'
                      : 'text-navy-muted hover:text-navy'
                  }`}
                >
                  {tab.label}
                  {activeTab === tab.id && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-spaq-cyan" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ============================================================ */}
        {/* 3. SECTION: OVERVIEW                                         */}
        {/* ============================================================ */}
        <section id="overview" className="py-16 md:py-24 bg-white border-b border-surface-border scroll-mt-28">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
              {/* 左見出し（デザイン案再現） */}
              <div className="lg:col-span-4 space-y-3">
                <span className="text-xs font-mono font-bold text-spaq-cyan-dark tracking-widest uppercase">
                  OVERVIEW
                </span>
                <h2 className="text-2xl sm:text-3xl md:text-[34px] font-bold text-navy tracking-tight leading-snug">
                  現場の電力を、
                  <br />
                  ひとつの制御へ。
                </h2>
                <p className="text-sm text-navy-muted leading-relaxed pt-2">
                  工場、冷凍冷蔵倉庫、物流センターなどの高圧受電施設で起きやすい「ピーク電力と作業快適性のジレンマ」。個別の機器ごとに動くのではなく、現場の状況を把握してひとつの知性で協調させます。
                </p>
              </div>

              {/* 右3ピラー（デザイン案再現） */}
              <div className="lg:col-span-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 divide-y md:divide-y-0 md:divide-x divide-surface-border">
                  <div className="pt-4 md:pt-0 md:px-5 first:pl-0">
                    <div className="text-xs font-mono font-bold text-spaq-cyan-dark mb-1">
                      01
                    </div>
                    <h3 className="text-base font-bold text-navy mb-2">先を読む</h3>
                    <p className="text-xs sm:text-sm text-navy-muted leading-relaxed">
                      需要の変化を捉え、必要な放電量を算出。短期・長期の変動を見極め、30分時限の着地点を判断します。
                    </p>
                  </div>

                  <div className="pt-4 md:pt-0 md:px-5">
                    <div className="text-xs font-mono font-bold text-spaq-cyan-dark mb-1">
                      02
                    </div>
                    <h3 className="text-base font-bold text-navy mb-2">設備をつなぐ</h3>
                    <p className="text-xs sm:text-sm text-navy-muted leading-relaxed">
                      太陽光・空調・蓄電池を協調制御。空調停止に頼り切らず、蓄電池を賢く組み合わせてピークを抑えます。
                    </p>
                  </div>

                  <div className="pt-4 md:pt-0 md:px-5">
                    <div className="text-xs font-mono font-bold text-spaq-cyan-dark mb-1">
                      03
                    </div>
                    <h3 className="text-base font-bold text-navy mb-2">安全を守る</h3>
                    <p className="text-xs sm:text-sm text-navy-muted leading-relaxed">
                      現場の状態に合わせて、制御と保護を判断。通信や機器の異常時は安全停止を最優先するフェイルセーフ設計です。
                    </p>
                  </div>
                </div>

                {/* 現行ライティング：施設が直面する課題 & 5段階協調シーケンス */}
                <div className="mt-10 pt-8 border-t border-surface-border space-y-4">
                  {/* アコーディオン：現場のよくある課題 */}
                  <div className="rounded-2xl border border-surface-border bg-surface-slate/60 overflow-hidden">
                    <button
                      onClick={() => toggleAccordion('overview-challenges')}
                      className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-surface-slate transition-colors"
                    >
                      <span className="text-sm font-bold text-navy">
                        こんな施設の電力課題に向き合います（よくある3つの悩み）
                      </span>
                      {openAccordions['overview-challenges'] ? (
                        <Minus className="w-4 h-4 text-spaq-cyan-dark shrink-0" />
                      ) : (
                        <Plus className="w-4 h-4 text-navy-muted shrink-0" />
                      )}
                    </button>
                    {openAccordions['overview-challenges'] && (
                      <div className="px-6 pb-6 pt-2 border-t border-surface-border/60 bg-white grid grid-cols-1 md:grid-cols-3 gap-4">
                        {[
                          {
                            title: 'デマコン警報のたびに現場が暑い・寒い',
                            desc: '契約電力を守るために既存のデマンドコントローラーが空調整備を強制停止してしまい、従業員の作業環境や来館者の快適性が損なわれている。',
                          },
                          {
                            title: '太陽光と蓄電池を入れたが連携していない',
                            desc: '太陽光・蓄電池・空調制御がそれぞれ別のルールで動いており、「ピークが来る前に蓄電池を使い切ってしまった」といったもったいない運用になっている。',
                          },
                          {
                            title: 'たった1回の30分ピークで基本料金が上がる',
                            desc: '夏場や冬場のほんの数十分の突発的な電力上昇によって、向こう1年間の契約電力（基本料金）が高止まりしてしまっている。',
                          },
                        ].map((issue, i) => (
                          <div key={i} className="p-4 rounded-xl bg-surface-slate border border-surface-border">
                            <span className="text-xs font-mono font-bold text-spaq-cyan-dark mb-1 block">
                              ISSUE 0{i + 1}
                            </span>
                            <div className="text-sm font-bold text-navy mb-1.5">{issue.title}</div>
                            <p className="text-xs text-navy-muted leading-relaxed">{issue.desc}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* アコーディオン：30分時限の5段階協調シーケンス */}
                  <div className="rounded-2xl border border-surface-border bg-surface-slate/60 overflow-hidden">
                    <button
                      onClick={() => toggleAccordion('overview-sequence')}
                      className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-surface-slate transition-colors"
                    >
                      <span className="text-sm font-bold text-navy">
                        30分のデマンド時限を見守る「5段階協調シーケンス」を見る
                      </span>
                      {openAccordions['overview-sequence'] ? (
                        <Minus className="w-4 h-4 text-spaq-cyan-dark shrink-0" />
                      ) : (
                        <Plus className="w-4 h-4 text-navy-muted shrink-0" />
                      )}
                    </button>
                    {openAccordions['overview-sequence'] && (
                      <div className="px-6 pb-6 pt-3 border-t border-surface-border/60 bg-white space-y-4">
                        <p className="text-xs sm:text-sm text-navy-muted leading-relaxed">
                          日本の高圧電力の基本料金は「30分間ごとの平均使用電力（デマンド値）」で決まります。SPAQ COREは30分の時限の最初から最後まで状況を見極め、蓄電池と空調が無理なく助け合う5つのステップで制御します。
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                          {[
                            {
                              phase: 'STEP 1',
                              time: '時限のスタート',
                              title: '状況の引き継ぎ・確認',
                              desc: '前の30分からの電力状況や蓄電池の残量をスムーズに引き継ぎ、無駄な停止・再起動を防ぎます。',
                            },
                            {
                              phase: 'STEP 2',
                              time: '序盤の見極め',
                              title: '初期需要と先行放電',
                              desc: 'ピーク超過が見込まれる場合は、蓄電池の放電を先行させ、いきなりの空調停止を抑えます。',
                            },
                            {
                              phase: 'STEP 3',
                              time: '中盤のメイン運用',
                              title: '蓄電池と空調の段階協調',
                              desc: '太陽光自家消費と蓄電池の効き目を確認。不足時のみ段階的に空調の緩和制御を組み合わせます。',
                            },
                            {
                              phase: 'STEP 4',
                              time: '終盤の余力活用',
                              title: '安全余裕での蓄電池回復',
                              desc: '残り時間と電力の余裕を厳密にチェック。安全な余裕がある時だけ蓄電池へ予測充電します。',
                            },
                            {
                              phase: 'STEP 5',
                              time: '次の30分への準備',
                              title: '時限境界のスムーズな橋渡し',
                              desc: '充電が次の時限のピーク要因にならないよう安全に収束させ、必要な抑制状態を引き継ぎます。',
                            },
                          ].map((step, idx) => (
                            <div key={idx} className="p-3.5 rounded-xl bg-surface-slate border border-surface-border">
                              <span className="text-[10px] font-mono font-bold text-spaq-cyan-dark bg-spaq-cyan-pale px-2 py-0.5 rounded-full inline-block mb-1">
                                {step.phase}
                              </span>
                              <div className="text-[11px] font-semibold text-spaq-gold-dark mb-1">{step.time}</div>
                              <div className="text-xs font-bold text-navy mb-1">{step.title}</div>
                              <p className="text-[11px] text-navy-muted leading-relaxed">{step.desc}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============================================================ */}
        {/* 4. SECTION: PREDICTIVE CONTROL                               */}
        {/* ============================================================ */}
        <section id="predictive" className="py-16 md:py-24 bg-surface-slate border-b border-surface-border scroll-mt-28">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
              {/* 左見出し（デザイン案再現） */}
              <div className="lg:col-span-5 space-y-4">
                <span className="text-xs font-mono font-bold text-spaq-cyan-dark tracking-widest uppercase">
                  PREDICTIVE CONTROL
                </span>
                <h2 className="text-2xl sm:text-3xl md:text-[34px] font-bold text-navy tracking-tight leading-snug">
                  ピークを先読みし、
                  <br />
                  次のピークにも備える。
                </h2>
                <p className="text-base sm:text-lg font-medium text-navy/90 leading-relaxed">
                  30分の電力量と残り時間から、
                  <br />
                  放電と充電を組み立てます。
                </p>
                <p className="text-xs sm:text-sm text-navy-muted leading-relaxed pt-1">
                  SPAQ COREは、現在の使用電力、直近の変化、30分間の電力量、蓄電池の残量を組み合わせ、時限終了時のデマンドを予測します。「いつ、どれだけ放電すればピークを抑えられるか」。そして、「次のピークに備えて、今どれだけ充電できるか」。変化する現場の状況に合わせて判断を更新し、ピーク抑制と蓄電池の残量回復を両立させます。
                </p>
              </div>

              {/* 右コンテンツ（デザイン案再現＋詳細トグル） */}
              <div className="lg:col-span-7 space-y-4">
                {/* 3つのコア機能リスト（画像案アイコン） */}
                <div className="bg-white rounded-2xl border border-surface-border p-6 shadow-soft space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-spaq-cyan-pale border border-spaq-cyan/30 flex items-center justify-center text-spaq-cyan-dark shrink-0 mt-0.5">
                      <TrendingUp className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-navy">需要を予測する</h3>
                      <p className="text-xs sm:text-sm text-navy-muted mt-0.5 leading-relaxed">
                        直近の変化と複数の時間軸を捉える。1分・3分の移動平均や上昇傾向を組み合わせ、30分後の着地点を判断します。
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 pt-4 border-t border-surface-border">
                    <div className="w-10 h-10 rounded-xl bg-spaq-cyan-pale border border-spaq-cyan/30 flex items-center justify-center text-spaq-cyan-dark shrink-0 mt-0.5">
                      <Clock className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-navy">必要な放電量を逆算する</h3>
                      <p className="text-xs sm:text-sm text-navy-muted mt-0.5 leading-relaxed">
                        目標と残り時間から出力を調整する。デマコン警報を待たずに放電を開始し、限られた蓄電池の能力を最適配分します。
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 pt-4 border-t border-surface-border">
                    <div className="w-10 h-10 rounded-xl bg-spaq-gold-pale border border-spaq-gold/40 flex items-center justify-center text-spaq-gold-dark shrink-0 mt-0.5">
                      <BatteryCharging className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-navy">余裕のある時間に充電する</h3>
                      <p className="text-xs sm:text-sm text-navy-muted mt-0.5 leading-relaxed">
                        安全余裕の範囲で蓄電池の残量を回復。充電によるデマンド上昇を避けつつ、次のピークに備えて自動で充電します。
                      </p>
                    </div>
                  </div>
                </div>

                {/* アコーディオン 1: 本来の需要を捉える予測技術 */}
                <div className="rounded-2xl border border-surface-border bg-white overflow-hidden shadow-soft">
                  <button
                    onClick={() => toggleAccordion('pred-nature')}
                    className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-surface-slate transition-colors"
                  >
                    <div className="flex items-center gap-2.5">
                      <span className="text-xs font-mono font-bold text-spaq-cyan-dark bg-spaq-cyan-pale px-2 py-0.5 rounded">
                        DEEP DIVE
                      </span>
                      <span className="text-sm font-bold text-navy">
                        本来の需要を捉える予測技術（01・04・05）
                      </span>
                    </div>
                    {openAccordions['pred-nature'] ? (
                      <Minus className="w-4 h-4 text-spaq-cyan-dark shrink-0" />
                    ) : (
                      <Plus className="w-4 h-4 text-navy-muted shrink-0" />
                    )}
                  </button>

                  {openAccordions['pred-nature'] && (
                    <div className="px-6 pb-6 pt-3 border-t border-surface-border space-y-4 bg-surface-slate/40">
                      {/* 01 */}
                      <div className="p-4 rounded-xl bg-white border border-surface-border">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-mono font-bold text-spaq-cyan-dark">01</span>
                          <span className="text-[11px] text-navy-muted font-medium">多角的な動向分析</span>
                        </div>
                        <h4 className="text-sm font-bold text-navy mb-2">複数の時間軸から、30分後の着地点を読む</h4>
                        <div className="space-y-1.5 text-xs text-navy-muted leading-relaxed">
                          <p>設備の起動による急な電力上昇と、操業に伴う緩やかな需要増加では、捉えるべき変化が異なります。</p>
                          <p>SPAQ COREは、直近の電力値に加え、1分・3分の移動平均、需要の上昇・下降傾向、30分の基準需要、デマンドコントローラーの予測値を組み合わせて分析。短期的な変化と継続的な負荷の両方を捉え、30分時限の終了時点で目標デマンドを超える可能性を判断します。さらに需要の変動に応じた予測誤差の余裕を加え、安全側の判断に活用します。</p>
                        </div>
                      </div>

                      {/* 04 */}
                      <div className="p-4 rounded-xl bg-white border border-spaq-cyan/40 ring-1 ring-spaq-cyan/20">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-mono font-bold text-spaq-cyan-dark">04</span>
                            <span className="text-[10px] font-bold text-spaq-cyan-dark bg-spaq-cyan-pale px-1.5 py-0.5 rounded flex items-center gap-1">
                              <Sparkles className="w-3 h-3 text-spaq-gold-dark" />
                              注目技術
                            </span>
                          </div>
                          <span className="text-[11px] text-navy-muted font-medium">需要復元・実態推定</span>
                        </div>
                        <h4 className="text-sm font-bold text-navy mb-2">制御によって下がった電力の、その先まで読む</h4>
                        <div className="space-y-1.5 text-xs text-navy-muted leading-relaxed">
                          <p>空調を抑制したり、蓄電池から放電したりすると、計測される受電電力は下がります。その低下には、施設本来の需要変化と、制御による効果の両方が含まれています。</p>
                          <p>SPAQ COREは、蓄電池の実際の放電電力を計測値へ加え戻し、制御前の需要を推定します。さらに、対応する空調計測データが得られる構成では、空調抑制によって減った電力や、復帰時に戻る負荷も予測に反映。制御の効果で一時的に低く見える需要を見極め、放電の早すぎる縮小や空調復帰時の需要増加に備えます。</p>
                        </div>
                      </div>

                      {/* 05 */}
                      <div className="p-4 rounded-xl bg-white border border-surface-border">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-mono font-bold text-spaq-cyan-dark">05</span>
                          <span className="text-[11px] text-navy-muted font-medium">設備保全・状態連動</span>
                        </div>
                        <h4 className="text-sm font-bold text-navy mb-2">蓄電池の残量と状態を踏まえた出力調整</h4>
                        <div className="space-y-1.5 text-xs text-navy-muted leading-relaxed">
                          <p>同じ蓄電池でも、残量や劣化状態によって使える電力量は変わります。</p>
                          <p>SPAQ COREは、設定容量、充電率（SOC）、健全度（SOH）、機器から取得する充放電可能出力を踏まえて制御量を調整。放電下限・充電上限を守りながら、現在のピーク抑制とその後の残量回復を組み立てます。</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* アコーディオン 2: 予測充放電の詳しい仕組み */}
                <div className="rounded-2xl border border-surface-border bg-white overflow-hidden shadow-soft">
                  <button
                    onClick={() => toggleAccordion('pred-details')}
                    className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-surface-slate transition-colors"
                  >
                    <div className="flex items-center gap-2.5">
                      <span className="text-xs font-mono font-bold text-spaq-gold-dark bg-spaq-gold-pale px-2 py-0.5 rounded">
                        ALGORITHM
                      </span>
                      <span className="text-sm font-bold text-navy">
                        予測充放電の詳しい仕組み（02・03・06）
                      </span>
                    </div>
                    {openAccordions['pred-details'] ? (
                      <Minus className="w-4 h-4 text-spaq-cyan-dark shrink-0" />
                    ) : (
                      <Plus className="w-4 h-4 text-navy-muted shrink-0" />
                    )}
                  </button>

                  {openAccordions['pred-details'] && (
                    <div className="px-6 pb-6 pt-3 border-t border-surface-border space-y-4 bg-surface-slate/40">
                      {/* 02 */}
                      <div className="p-4 rounded-xl bg-white border border-surface-border">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-mono font-bold text-spaq-cyan-dark">02</span>
                          <span className="text-[11px] text-navy-muted font-medium">先行放電・最適配分</span>
                        </div>
                        <h4 className="text-sm font-bold text-navy mb-2">残り時間から逆算する、先行放電</h4>
                        <div className="space-y-1.5 text-xs text-navy-muted leading-relaxed">
                          <p>ピーク抑制では、放電するタイミングと出力の両方が重要です。</p>
                          <p>SPAQ COREは、予測される目標超過と時限終了までの残り時間から、必要な放電量を逆算。デマンドコントローラーの警報を待たずに放電を開始できるため、超過が見込まれる段階から蓄電池を活用します。放電量は蓄電池の残量、使用可能な電力量、機器の最大出力、現在の受電電力に基づいて調整し、限られた能力を必要なタイミングへ配分します。</p>
                        </div>
                      </div>

                      {/* 03 */}
                      <div className="p-4 rounded-xl bg-white border border-spaq-cyan/40 ring-1 ring-spaq-cyan/20">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-mono font-bold text-spaq-cyan-dark">03</span>
                            <span className="text-[10px] font-bold text-spaq-cyan-dark bg-spaq-cyan-pale px-1.5 py-0.5 rounded flex items-center gap-1">
                              <Sparkles className="w-3 h-3 text-spaq-gold-dark" />
                              注目技術
                            </span>
                          </div>
                          <span className="text-[11px] text-navy-muted font-medium">残量回復・余力計算</span>
                        </div>
                        <h4 className="text-sm font-bold text-navy mb-2">電力量の余裕を計算する、予測充電</h4>
                        <div className="space-y-1.5 text-xs text-navy-muted leading-relaxed">
                          <p>放電した蓄電池を回復させる充電も、施設の使用電力を増やします。SPAQ COREは、充電によるデマンド上昇まで含めて制御します。</p>
                          <p>30分間で使用できる電力量を基準に、すでに使用した電力量と、時限終了までに必要と予測される電力量を差し引き、安全余裕を確保。その範囲内で蓄電池へ戻せる充電量を算出します。需要下降時は充電機会を活用し、上昇時は抑制。終盤の余力も利用しながら次のピークに備えます。</p>
                        </div>
                      </div>

                      {/* 06 */}
                      <div className="p-4 rounded-xl bg-white border border-surface-border">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-mono font-bold text-spaq-cyan-dark">06</span>
                          <span className="text-[11px] text-navy-muted font-medium">シャドーモード・ログ追跡</span>
                        </div>
                        <h4 className="text-sm font-bold text-navy mb-2">予測・判断・結果を記録し、検証できる制御</h4>
                        <div className="space-y-1.5 text-xs text-navy-muted leading-relaxed">
                          <p>SPAQ COREは、予測値、推奨放電量、制御判断、実績を記録します。実機へ充放電指令を出さずに予測計算と記録を行う「シャドーモード」により、現場の需要データを使った事前評価が可能です。</p>
                          <p>予測と実績の差、目標超過の見逃し、従来の予測値との比較を通じて制御の妥当性を検証。導入条件に合わせた調整と、運用後の客観的な評価につなげます。</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <p className="text-xs text-navy-muted leading-relaxed pt-1">
                  ※利用できる機能・制御範囲は、接続機器、取得可能なデータ、設備構成および運用設定によって異なります。ピーク抑制効果や予測精度は、施設の負荷特性と運用条件に基づいて評価します。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ============================================================ */}
        {/* 5. SECTION: EDGE & SAFETY                                    */}
        {/* ============================================================ */}
        <section id="safety" className="py-16 md:py-24 bg-white border-b border-surface-border scroll-mt-28">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
              {/* 左見出し（デザイン案再現） */}
              <div className="lg:col-span-5 space-y-4">
                <span className="text-xs font-mono font-bold text-spaq-cyan-dark tracking-widest uppercase">
                  EDGE & SAFETY
                </span>
                <h2 className="text-2xl sm:text-3xl md:text-[34px] font-bold text-navy tracking-tight leading-snug">
                  判断は現場で。
                  <br />
                  安全を優先して動く。
                </h2>
                <p className="text-sm text-navy-muted leading-relaxed">
                  SPAQ COREは施設内に設置する制御ボックス（エッジ端末）が受電点や太陽光PCS、蓄電池、デマンドコントローラーと直接通信して制御を行います。
                </p>
                <p className="text-xs sm:text-sm text-navy-muted leading-relaxed">
                  充放電の判断では、機器の出力制限、蓄電池残量、逆潮流防止の条件を優先。機器間の通信異常や計測データの欠損・鮮度低下を検知した場合は、充放電の抑止・停止へ移行します。また、30分時限の切り替わりでは、時計の差や通信遅延を考慮して充電を安全に収束させます。
                </p>
              </div>

              {/* 右リストリンク（デザイン案再現） */}
              <div className="lg:col-span-7 space-y-3">
                {[
                  {
                    id: 'edge',
                    title: '現場エッジで監視・制御',
                    summary: 'クラウド通信が一時的に不安定でも、現場端末で自律的に安全監視と制御を継続します。',
                    details: '平常時は自家消費最適化とピークカット、万一の停電時には非常用電源（BCP）として蓄電池残量を優先確保する運用切り替えにも対応します。',
                  },
                  {
                    id: 'anomaly',
                    title: '通信・機器の異常時は保護動作',
                    summary: '機器出力制限、蓄電池残量、逆潮流防止の条件を常に最優先するフェイルセーフ設計です。',
                    details: '通信断やパルス欠損を検知した際は、即座に充放電を安全停止・待機へ移行。設備への過負荷や無駄な充放電を徹底的に防ぎます。',
                  },
                  {
                    id: 'shadow',
                    title: 'シャドーモードで事前検証',
                    summary: '実機へ充放電指令を出さずに、現場のリアルな需要データで予測精度と制御判断を事前評価できます。',
                    details: '導入前に現場データを蓄積し、シミュレーションとの誤差や目標達成度を検証した上で、安心して実制御へ移行いただけます。',
                  },
                ].map((item) => (
                  <div
                    key={item.id}
                    className="rounded-2xl border border-surface-border bg-surface-slate/60 hover:bg-surface-slate transition-all overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenSafetyItem(openSafetyItem === item.id ? null : item.id)}
                      className="w-full p-5 sm:p-6 flex items-center justify-between text-left"
                    >
                      <div>
                        <div className="text-base font-bold text-navy flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-spaq-cyan-dark" />
                          <span>{item.title}</span>
                        </div>
                        <p className="text-xs sm:text-sm text-navy-muted mt-1 leading-relaxed">
                          {item.summary}
                        </p>
                      </div>
                      <ArrowRight
                        className={`w-4 h-4 text-spaq-cyan-dark transition-transform shrink-0 ml-4 ${
                          openSafetyItem === item.id ? 'rotate-90' : ''
                        }`}
                      />
                    </button>
                    {openSafetyItem === item.id && (
                      <div className="px-6 pb-5 pt-1 border-t border-surface-border/50 bg-white text-xs sm:text-sm text-navy-muted leading-relaxed">
                        {item.details}
                      </div>
                    )}
                  </div>
                ))}

                {/* 導入条件・事前確認事項カード */}
                <div className="p-6 rounded-2xl bg-surface-sky border border-spaq-cyan/30 mt-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-bold text-spaq-cyan-dark uppercase tracking-wider">
                      REQUIREMENTS
                    </span>
                    <Badge variant="cyan" size="sm">事前確認事項</Badge>
                  </div>
                  <h3 className="text-sm font-bold text-navy mb-2">導入条件・現地での事前確認事項</h3>
                  <ul className="space-y-2 text-xs sm:text-sm text-navy">
                    <li className="flex items-start gap-2">
                      <span className="text-spaq-cyan-dark font-bold">•</span>
                      <span>受変電設備（キュービクル等）のパルス取り出し・電力計測ポイントの確認</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-spaq-cyan-dark font-bold">•</span>
                      <span>既存または新設する太陽光PCS・蓄電池・デマンドコントローラーの通信仕様（Modbus等）</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-spaq-cyan-dark font-bold">•</span>
                      <span>空調系統の優先度（「絶対に止められないエリア」と「緩和制御できるエリア」の整理）</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============================================================ */}
        {/* 6. SECTION: CASE & MODEL                                     */}
        {/* ============================================================ */}
        <section id="cases" className="py-16 md:py-24 bg-surface-slate border-b border-surface-border scroll-mt-28">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10 space-y-2">
              <span className="text-xs font-mono font-bold text-spaq-cyan-dark tracking-widest uppercase">
                CASE & MODEL
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-[34px] font-bold text-navy tracking-tight">
                現場での活用と、導入前の検証。
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* カード 1: 冷凍冷蔵倉庫の協調運用 */}
              <div className="bg-white rounded-3xl border border-surface-border overflow-hidden shadow-soft hover:shadow-soft-lg transition-all group flex flex-col justify-between">
                <div>
                  <div className="relative aspect-[16/9] w-full bg-slate-200 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=800&q=80"
                      alt="冷凍冷蔵倉庫の協調運用"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-transparent pointer-events-none" />
                    <span className="absolute bottom-3 right-3 text-[11px] text-white/80 bg-navy/60 backdrop-blur-xs px-2 py-0.5 rounded font-mono">
                      写真はイメージ
                    </span>
                  </div>
                  <div className="p-6 sm:p-7 space-y-3">
                    <div className="flex items-center gap-2">
                      <Badge variant="gold" size="sm">モデル試算</Badge>
                      <span className="text-xs text-navy-muted">高圧受電 450kW</span>
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-navy group-hover:text-spaq-cyan-dark transition-colors">
                      冷凍冷蔵倉庫の協調運用
                    </h3>
                    <p className="text-xs sm:text-sm text-navy-muted leading-relaxed">
                      太陽光・空調・蓄電池の組み合わせを検証。夏場のデマンドピーク抑制（約65kW削減試算）と庫内温度の安定維持を両立する協調モデル。
                    </p>
                  </div>
                </div>

                <div className="px-6 pb-6 pt-2">
                  <Link
                    to="/cases/cold-storage-simulation-model"
                    className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold text-spaq-cyan-dark hover:text-spaq-cyan transition-colors"
                  >
                    <span>試算モデルを見る</span>
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>

              {/* カード 2: 商業・業務施設のデマンド管理 */}
              <div className="bg-white rounded-3xl border border-surface-border overflow-hidden shadow-soft hover:shadow-soft-lg transition-all group flex flex-col justify-between">
                <div>
                  <div className="relative aspect-[16/9] w-full bg-slate-200 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=800&q=80"
                      alt="商業・業務施設のデマンド管理"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-transparent pointer-events-none" />
                    <span className="absolute bottom-3 right-3 text-[11px] text-white/80 bg-navy/60 backdrop-blur-xs px-2 py-0.5 rounded font-mono">
                      写真はイメージ
                    </span>
                  </div>
                  <div className="p-6 sm:p-7 space-y-3">
                    <div className="flex items-center gap-2">
                      <Badge variant="cyan" size="sm">運用検証</Badge>
                      <span className="text-xs text-navy-muted">高圧受電 500kW</span>
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-navy group-hover:text-spaq-cyan-dark transition-colors">
                      商業・業務施設のデマンド管理
                    </h3>
                    <p className="text-xs sm:text-sm text-navy-muted leading-relaxed">
                      快適性に配慮した段階的なピーク抑制。デマンド監視装置と電源の協調運用検証により、テナント環境を守りながら基本料金を抑制。
                    </p>
                  </div>
                </div>

                <div className="px-6 pb-6 pt-2">
                  <Link
                    to="/cases/commercial-facility-ems-operation"
                    className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold text-spaq-cyan-dark hover:text-spaq-cyan transition-colors"
                  >
                    <span>運用事例を見る</span>
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============================================================ */}
        {/* 7. SECTION: FAQ                                              */}
        {/* ============================================================ */}
        <section id="faq" className="py-16 md:py-24 bg-white border-b border-surface-border scroll-mt-28">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
              {/* 左見出し（デザイン案再現） */}
              <div className="lg:col-span-5 space-y-3">
                <span className="text-xs font-mono font-bold text-spaq-cyan-dark tracking-widest uppercase">
                  FAQ
                </span>
                <h2 className="text-2xl sm:text-3xl md:text-[34px] font-bold text-navy tracking-tight leading-snug">
                  導入前に、
                  <br />
                  確認したいこと。
                </h2>
                <p className="text-sm text-navy-muted leading-relaxed pt-1">
                  ご検討時によくいただくご質問をまとめています。設備構成や責任分界に関するご不明な点もお気軽にご相談ください。
                </p>
              </div>

              {/* 右アコーディオンリスト（デザイン案再現） */}
              <div className="lg:col-span-7 space-y-3">
                {SPAQ_CORE_FAQS.map((faq, idx) => (
                  <div
                    key={idx}
                    className="rounded-2xl border border-surface-border bg-surface-slate/50 hover:bg-surface-slate transition-all overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                      className="w-full p-5 sm:p-6 flex items-start justify-between text-left gap-4"
                    >
                      <span className="text-sm sm:text-base font-bold text-navy leading-snug">
                        {faq.q}
                      </span>
                      {openFaq === idx ? (
                        <Minus className="w-4 h-4 text-spaq-cyan-dark shrink-0 mt-1" />
                      ) : (
                        <Plus className="w-4 h-4 text-navy-muted shrink-0 mt-1" />
                      )}
                    </button>
                    {openFaq === idx && (
                      <div className="px-6 pb-6 pt-1 border-t border-surface-border/50 bg-white text-xs sm:text-sm text-navy-muted leading-relaxed">
                        {faq.a}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ============================================================ */}
        {/* 8. SECTION: CONTACT CTA (Dark Editorial Theme)               */}
        {/* ============================================================ */}
        <section className="bg-[#061523] text-white py-16 md:py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_70%_at_50%_120%,rgba(37,168,208,0.12),rgba(255,255,255,0))] pointer-events-none" />

          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              {/* 左見出し（デザイン案再現） */}
              <div className="lg:col-span-6 space-y-4">
                <span className="text-xs font-mono font-bold text-spaq-cyan-bright tracking-widest uppercase">
                  CONTACT
                </span>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-white leading-tight">
                  施設に合った電力制御を、ともに。
                </h2>
                <p className="text-sm sm:text-base text-slate-300 leading-relaxed max-w-xl">
                  これから太陽光・蓄電池を検討される施設様も、すでに導入済みの設備をうまく連携させたい施設様も、現状のデマンドデータ診断から丁寧にご案内いたします。
                </p>
              </div>

              {/* 右カード型アクションボタン（デザイン案再現） */}
              <div className="lg:col-span-6 space-y-3.5">
                <Link
                  to="/contact?type=spaq-core"
                  className="w-full p-5 sm:p-6 rounded-2xl bg-white/5 border border-white/15 hover:border-spaq-cyan hover:bg-white/10 transition-all flex items-center justify-between group shadow-lg"
                >
                  <div>
                    <div className="text-base sm:text-lg font-bold text-white group-hover:text-spaq-cyan-bright transition-colors">
                      SPAQ COREの導入相談
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      施設条件やデマンドデータの確認・概算シミュレーション
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-spaq-cyan-bright transition-transform group-hover:translate-x-1 shrink-0 ml-4" />
                </Link>

                <Link
                  to="/partners"
                  className="w-full p-5 sm:p-6 rounded-2xl bg-white/5 border border-white/15 hover:border-spaq-cyan hover:bg-white/10 transition-all flex items-center justify-between group shadow-lg"
                >
                  <div>
                    <div className="text-base sm:text-lg font-bold text-white group-hover:text-spaq-cyan-bright transition-colors">
                      設備連携・協業のご相談
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      機器メーカー・設備工事・電力小売事業者様とのパートナーシップ
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-spaq-cyan-bright transition-transform group-hover:translate-x-1 shrink-0 ml-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
